import React from "react";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { MenuItem, FormGroup, AnchorButton } from "@blueprintjs/core";
import { ItemPredicate, ItemRenderer, MultiSelect } from "@blueprintjs/select";
import { useField } from "formik";
import { Company, ProjectVariable, UserInput } from "types";
import { useReference, ConfigurationItem } from "hooks/useReference";
import { SnContext } from "utils/context";
import { formikCompanyFieldName, getFormikFieldName } from "utils/utils";

type Props = { variable: ProjectVariable };

export default function ListField({ variable }: Props) {
    const { tableRef, label, required, name } = variable;
    const [company] = useField<Company>(formikCompanyFieldName);
    const companyRef = useRef<Company | undefined>(company.value);

    const [field, , { setValue }] = useField<UserInput>(getFormikFieldName(name));

    const [data, loading, error] = useReference(company.value.id, variable.tableRef!);
    const [selected, setSelected] = useState<ConfigurationItem[]>([]);
    const snContext = useContext(SnContext);
    const snUri = `https://${snContext}.service-now.com`;
    //todo: figure out why clear button is triggered by individual tag clear
    // const handleClear = () => setSelected([]);
    // const clearButton = selected.length > 0 ? <Button icon="cross" minimal={true} onClick={handleClear} /> : undefined;

    useEffect(() => {
        if (companyRef.current?.id !== company.value.id) {
            const { type, label } = { ...field.value };
            setValue({ name: [], value: [], type, label });
            companyRef.current = company.value;
        }
    }, [company, field, setValue]);

    const isItemSelected = useCallback(
        (item: ConfigurationItem) => {
            return selected.indexOf(item) !== -1;
        },
        [selected]
    );

    // formik object needs to be update separate from the selected state
    // because of how ci names and sysids are stored on the backend.
    //todo: change the render model to use formik state so we're not storing duplicate state
    const updateFormikValues = useCallback(
        (updated: ConfigurationItem[]) => {
            const { type, label } = { ...field.value };
            setValue({
                name: updated.map((c) => c.name),
                value: updated.map((c) => c.sysId),
                type,
                label
            });
        },
        [field, setValue]
    );
    //todo: use formik here
    const handleSelect = useCallback(
        (item) => {
            const updated = selected.includes(item)
                ? selected.filter((i) => i.sysId !== item.sysId)
                : [...selected, item];
            setSelected(updated);
            updateFormikValues(updated);
        },
        [selected, updateFormikValues]
    );
    const handleRemove = useCallback(
        (item: ConfigurationItem) => {
            const updated = selected.filter((i) => i.sysId !== item.sysId);
            setSelected(updated);
            updateFormikValues(updated);
        },
        [selected, updateFormikValues]
    );

    const renderList: ItemRenderer<ConfigurationItem> = useCallback(
        (item, { modifiers, handleClick }) => {
            if (!modifiers.matchesPredicate) {
                return null;
            }
            return (
                <MenuItem
                    active={modifiers.active}
                    icon={isItemSelected(item) ? "tick" : "blank"}
                    key={item.sysId}
                    onClick={handleClick}
                    text={item.name}
                    shouldDismissPopover={false}
                    //!todo fix <a> descendant
                    labelElement={
                        <AnchorButton
                            intent="primary"
                            minimal
                            icon="link"
                            target="_blank"
                            href={`${snUri}/nav_to.do?uri=${tableRef}.do?sys_id=${item.sysId}`}
                            // prevent click on parent
                            onClick={(e: React.MouseEvent<HTMLElement, MouseEvent>) => e.stopPropagation()}
                        />
                    }
                />
            );
        },
        [isItemSelected, snUri, tableRef]
    );

    return (
        <FormGroup
            inline={false}
            label={label}
            labelFor="text-input"
            labelInfo={required ? "*" : undefined}
            disabled={!!error || loading}
        >
            <ListCollectorSelect
                fill
                itemRenderer={renderList}
                itemsEqual={areRefsEqual}
                items={data ? data : []}
                noResults={<MenuItem disabled={true} text="No results." />}
                selectedItems={selected}
                onItemSelect={handleSelect}
                onRemove={handleRemove}
                popoverProps={{ minimal: true, popoverClassName: "custom-select" }}
                tagRenderer={renderTag}
                itemPredicate={filterItems}
            />
        </FormGroup>
    );
}

const ListCollectorSelect = MultiSelect.ofType<ConfigurationItem>();
const filterItems: ItemPredicate<ConfigurationItem> = (query, item) => {
    return item.name.toLowerCase().indexOf(query.toLowerCase()) >= 0;
};
const areRefsEqual = (refA: ConfigurationItem, refB: ConfigurationItem) => refA.sysId === refB.sysId;
const renderTag = (item: ConfigurationItem) => item.name;
